const APP_INSIGHTS_INGESTION_PATTERN =
    /applicationinsights\.azure\.com|dc\.services\.visualstudio\.com|rt\.services\.visualstudio\.com|js\.monitor\.azure\.com/i;

const GUARD_INSTALLED_KEY = '__lcAppInsightsIngestionGuardInstalled';

let ingestionAllowed = false;

const resolveRequestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') {
        return input;
    }
    if (input instanceof URL) {
        return input.toString();
    }
    return input.url;
};

export const isAppInsightsIngestionUrl = (url?: string): boolean =>
    !!url && APP_INSIGHTS_INGESTION_PATTERN.test(url);

export const setAppInsightsIngestionAllowed = (allowed: boolean): void => {
    ingestionAllowed = allowed;
};

export const isAppInsightsIngestionAllowed = (): boolean => ingestionAllowed;

export const shouldBlockAppInsightsIngestion = (url?: string): boolean =>
    !ingestionAllowed && isAppInsightsIngestionUrl(url);

const blockedFetchResponse = (): Response => {
    if (typeof Response !== 'undefined') {
        return new Response(null, { status: 204, statusText: 'No Content' });
    }

    return {
        ok: true,
        status: 204,
        statusText: 'No Content',
        redirected: false,
        type: 'default',
        url: '',
        body: null,
        bodyUsed: false,
        headers: { get: () => null },
        clone() { return this; },
        arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)),
        blob: () => Promise.resolve(new Blob([])),
        formData: () => Promise.resolve(new FormData()),
        json: () => Promise.resolve(null),
        text: () => Promise.resolve('')
    } as unknown as Response;
};

export const installAppInsightsIngestionGuard = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const browserWindow = window as Window & { [GUARD_INSTALLED_KEY]?: boolean };
    if (browserWindow[GUARD_INSTALLED_KEY]) {
        return;
    }
    browserWindow[GUARD_INSTALLED_KEY] = true;

    const originalFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : undefined;
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        if (shouldBlockAppInsightsIngestion(resolveRequestUrl(input))) {
            return Promise.resolve(blockedFetchResponse());
        }
        if (!originalFetch) {
            return Promise.reject(new TypeError('Failed to fetch'));
        }
        return originalFetch(input, init);
    }) as typeof window.fetch;

    if (typeof XMLHttpRequest !== 'undefined') {
        const originalOpen = XMLHttpRequest.prototype.open;
        const originalSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function(this: XMLHttpRequest, method: string, url: string | URL, async?: boolean, username?: string | null, password?: string | null) {
            (this as XMLHttpRequest & { __lcAppInsightsUrl?: string }).__lcAppInsightsUrl = String(url);
            if (arguments.length > 2) {
                return originalOpen.call(this, method, url, async as boolean, username, password);
            }
            return originalOpen.call(this, method, url);
        };

        XMLHttpRequest.prototype.send = function(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
            if (shouldBlockAppInsightsIngestion((this as XMLHttpRequest & { __lcAppInsightsUrl?: string }).__lcAppInsightsUrl)) {
                return;
            }
            return originalSend.call(this, body);
        };
    }

    if (typeof navigator.sendBeacon === 'function') {
        const originalBeacon = navigator.sendBeacon.bind(navigator);
        navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
            if (shouldBlockAppInsightsIngestion(String(url))) {
                return true;
            }
            return originalBeacon(url, data);
        };
    }
};

installAppInsightsIngestionGuard();
